import { useState } from "react";
import { toast } from "react-hot-toast";
import { useAdminLoans, useUpdateAdminLoanStatus } from "../../../api/admin";

export default function AdminLoans() {
  const [selectedLoan, setSelectedLoan] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: loans, isLoading, refetch } = useAdminLoans();
  const updateStatusMutation = useUpdateAdminLoanStatus();

  const handleStatusUpdate = async (loanId: number, newStatus: string) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: loanId,
        data: { status: newStatus },
      });
      toast.success("Loan status updated successfully");
      refetch();
      setIsModalOpen(false);
      setSelectedLoan(null);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update loan status",
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "active":
        return "bg-purple-100 text-purple-800";
      case "paid":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
      case "defaulted":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Extract loans and stats from nested response
  const loansData = loans?.results?.data || [];
  const apiStats = loans?.results || {};

  const filteredLoans = loansData.filter((loan: any) => {
    if (statusFilter === "all") return true;
    return loan.status?.toLowerCase() === statusFilter;
  });

  const stats = {
    total: apiStats.total_loan_count || 0,
    pending: loansData.filter((l: any) => l.status === "pending").length || 0,
    active: apiStats.active_loans_count || 0,
    paid: loansData.filter((l: any) => l.status === "paid").length || 0,
    defaulted:
      loansData.filter((l: any) => l.status === "defaulted").length || 0,
    totalAmount: apiStats.total_loan_amount || 0,
    totalOutstanding: apiStats.outstanding_amount || 0,
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Loan Management</h1>
        <p className="text-gray-600 mt-1">Manage and monitor all user loans</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Loans</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-line text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Loans</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.active}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-hand-coin-line text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Disbursed</p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{stats.totalAmount.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-2xl font-bold text-red-600">
                ₦{stats.totalOutstanding.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-error-warning-line text-red-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            "all",
            "pending",
            "approved",
            "active",
            "paid",
            "rejected",
            "defaulted",
          ].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Loans Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <i className="ri-loader-4-line animate-spin text-4xl text-purple-600"></i>
          </div>
        ) : filteredLoans && filteredLoans.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Repayment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLoans.map((loan: any) => (
                  <tr key={loan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {loan.user?.username || loan.user?.email || "N/A"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {loan.user?.email || ""}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ₦{Number(loan.amount).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Interest: {loan.interest_rate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ₦{Number(loan.repayment_amount).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Paid: ₦{Number(loan.amount_paid).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(loan.status)}`}
                      >
                        {loan.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(loan.due_date)}
                      {loan.is_overdue && (
                        <span className="block text-xs text-red-600 font-medium mt-1">
                          Overdue
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => {
                          setSelectedLoan(loan);
                          setIsModalOpen(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 font-medium"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <i className="ri-file-list-3-line text-5xl text-gray-300 mb-3"></i>
            <p className="text-gray-500">No loans found</p>
          </div>
        )}
      </div>

      {/* Loan Details Modal */}
      {isModalOpen && selectedLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Loan Details
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    ID: {selectedLoan.id}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedLoan(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  User Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm">
                    <span className="font-medium">Username:</span>{" "}
                    {selectedLoan.user?.username}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Email:</span>{" "}
                    {selectedLoan.user?.email}
                  </p>
                </div>
              </div>

              {/* Loan Details */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Loan Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">Loan Amount</p>
                    <p className="text-lg font-bold text-gray-900">
                      ₦{Number(selectedLoan.amount).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">Interest Rate</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedLoan.interest_rate}%
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">Repayment Amount</p>
                    <p className="text-lg font-bold text-gray-900">
                      ₦{Number(selectedLoan.repayment_amount).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">Amount Paid</p>
                    <p className="text-lg font-bold text-purple-600">
                      ₦{Number(selectedLoan.amount_paid).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">Remaining Balance</p>
                    <p className="text-lg font-bold text-red-600">
                      ₦
                      {(
                        Number(selectedLoan.repayment_amount) -
                        Number(selectedLoan.amount_paid)
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600">Due Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(selectedLoan.due_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Update */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Update Status
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    "pending",
                    "approved",
                    "active",
                    "paid",
                    "rejected",
                    "defaulted",
                  ].map((status) => (
                    <button
                      key={status}
                      onClick={() =>
                        handleStatusUpdate(selectedLoan.id, status)
                      }
                      disabled={
                        updateStatusMutation.isPending ||
                        selectedLoan.status === status
                      }
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        selectedLoan.status === status
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
